/**
 * Ride Operation Confirmation Dialog Component
 * 
 * Modal dialog for confirming ride operations (upload, download) with safety checks.
 * Similar to OperationConfirmationDialog but designed for rides instead of routes.
 * 
 * @module RideOperationConfirmationDialog
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  Divider,
  TextField,
  Card,
  CardContent,
  Stack,
} from '@mui/material';

import type { Environment } from '../EnvironmentSelector';
import { getEnvironmentDisplayName } from '../EnvironmentSelector';
import type { RideSchema } from '../../../../types/simulation';
import type { UATRideEntry, UATRideDetailedEntry } from '../../../../services/fixedRouteUatClient';
import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
/**
 * Operation type
 */
export type RideOperationType = 'upload' | 'download' | 'delete';

/**
 * Simplified ride info for dialog display
 */
interface RideInfo {
  id: string;
  name: string;
  pickupAddress?: string;
  dropoffAddress?: string;
  departureTime?: string;
  passengers?: number;
  status?: string;
  tripType?: string;
}

/**
 * Props for RideOperationConfirmationDialog component
 */
export interface RideOperationConfirmationDialogProps {
  /** Operation type */
  operationType: RideOperationType;
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Rides to be operated on */
  rides: RideInfo[];
  /** Selected environment */
  environment: Environment;
  /** Whether operation is in progress */
  isProcessing?: boolean;
  /** Operation progress (0-100) */
  progress?: number;
  /** Current ride being processed */
  currentRide?: string;
  /** Callback when operation is confirmed */
  onConfirm: (serviceDate?: string) => void;
  /** Default service date for upload operations (YYYY-MM-DD) */
  defaultServiceDate?: string;
}

/**
 * RideOperationConfirmationDialog Component
 * 
 * Modal dialog for confirming ride operations with safety checks.
 */
export const RideOperationConfirmationDialog: React.FC<RideOperationConfirmationDialogProps> = ({
  operationType,
  open,
  onClose,
  rides,
  environment,
  isProcessing = false,
  progress = 0,
  currentRide,
  onConfirm,
  defaultServiceDate,
}) => {
  const { t } = useTranslation('mapTransfer');
  const [acknowledged, setAcknowledged] = useState(false);
  const [serviceDate, setServiceDate] = useState<string>(() => {
    // Default to today's date in YYYY-MM-DD format
    if (defaultServiceDate) {
      return defaultServiceDate;
    }
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      // Reset service date to default when dialog opens
      if (defaultServiceDate) {
        setServiceDate(defaultServiceDate);
      } else {
        const today = new Date();
        setServiceDate(today.toISOString().split('T')[0]);
      }
    }
  }, [open, defaultServiceDate]);

  const handleConfirm = () => {
    if (acknowledged) {
      // For upload operations, pass the service date
      if (operationType === 'upload') {
        onConfirm(serviceDate);
      } else {
        // For download and delete operations, no service date needed
        onConfirm();
      }
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  // Validate service date for upload operations
  const isServiceDateValid = operationType === 'upload' 
    ? serviceDate && /^\d{4}-\d{2}-\d{2}$/.test(serviceDate)
    : true;

  const canProceed = acknowledged && !isProcessing && isServiceDateValid;

  // Get operation-specific details
  const envName = getEnvironmentDisplayName(environment);
  const getOperationDetails = () => {
    switch (operationType) {
      case 'upload':
        return {
          title: t('rides.dialog.uploadTitle'),
          icon: <Icon size={20} name="upload" />,
          warningMessage: t('rides.dialog.uploadWarning', { env: envName }),
          actionButtonText: t('rides.dialog.uploadButton'),
          actionButtonIcon: <Icon size={20} name="upload" />,
          acknowledgmentText: t('rides.dialog.uploadAcknowledgment', { env: envName }),
        };
      case 'download':
        return {
          title: t('rides.dialog.downloadTitle'),
          icon: <Icon size={20} d={ICON_FALLBACKS.CloudDownload} />,
          warningMessage: t('rides.dialog.downloadWarning', { env: envName }),
          actionButtonText: t('rides.dialog.downloadButton'),
          actionButtonIcon: <Icon size={20} d={ICON_FALLBACKS.CloudDownload} />,
          acknowledgmentText: t('rides.dialog.downloadAcknowledgment', { env: envName }),
          severity: 'warning' as const,
        };
      case 'delete':
        return {
          title: t('rides.dialog.deleteTitle'),
          icon: <Icon size={20} name="trash" />,
          warningMessage: t('rides.dialog.deleteWarning', { env: envName }),
          actionButtonText: t('rides.dialog.deleteButton'),
          actionButtonIcon: <Icon size={20} name="trash" />,
          acknowledgmentText: t('rides.dialog.deleteAcknowledgment', { env: envName }),
          severity: 'error' as const,
        };
      default:
        // TypeScript exhaustiveness check - should never reach here
        const _exhaustive: never = operationType;
        throw new Error(t('rides.dialog.unknownOperation', { value: _exhaustive }));
    }
  };

  const details = getOperationDetails();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isProcessing}
      sx={{ zIndex: 2500 }}
      PaperProps={{
        sx: {
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon size={20} name="triangle-exclamation" />
          <Typography variant="h6">{details.title}</Typography>
          <Chip
            label={getEnvironmentDisplayName(environment)}
            size="small"
            color={environment === 'UAT' ? 'success' : 'primary'}
            sx={{ ml: 'auto' }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Warning Alert */}
        <Alert severity={details.severity || 'warning'} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {t('rides.dialog.reviewBeforeProceeding')}
          </Typography>
          <Typography variant="body2">
            {details.warningMessage}
          </Typography>
        </Alert>

        {/* Operation Summary */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {operationType === 'upload'
              ? t('rides.dialog.youAreAboutToUpload')
              : operationType === 'download'
              ? t('rides.dialog.youAreAboutToDownload')
              : t('rides.dialog.youAreAboutToDelete')}
          </Typography>
          <Typography variant="h6" color="primary">
            {t('rides.dialog.ridesCount', { count: rides.length })}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Service Date Selector (Upload Only) */}
        {operationType === 'upload' && (
          <Box 
            sx={{ 
              mb: 2,
              p: 2,
              backgroundColor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('rides.dialog.serviceDate')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {t('rides.dialog.serviceDateHelp')}
            </Typography>
            <TextField
              label={t('rides.dialog.dateLabel')}
              type="date"
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={isProcessing}
              required
              error={!isServiceDateValid && serviceDate !== ''}
              helperText={!isServiceDateValid && serviceDate !== ''
                ? t('rides.dialog.validDateError')
                : t('rides.dialog.serviceDateHelper')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </Box>
        )}

        {/* Ride List */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
            {operationType === 'upload'
              ? t('rides.dialog.ridesToUploadHeading', { count: rides.length })
              : operationType === 'delete'
              ? t('rides.dialog.ridesToDeleteHeading', { count: rides.length })
              : t('rides.dialog.ridesToDownloadHeading', { count: rides.length })}
          </Typography>
          <Box
            sx={{
              maxHeight: '450px',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              pr: 0.5,
            }}
          >
            {rides.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('rides.dialog.noRidesSelected')}
                </Typography>
              </Box>
            ) : (
              rides.map((ride, index) => (
                <Card
                  key={`${ride.id}-${index}`}
                  variant="outlined"
                  sx={{
                    backgroundColor: 'background.paper',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 1,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1.5}>
                      {/* Ride Name */}
                      <Typography variant="subtitle2" fontWeight={600} color="primary">
                        {ride.name || t('rides.dialog.rideFallback', { index: index + 1 })}
                      </Typography>
                    
                    {/* Ride Details */}
                    <Stack spacing={1.5}>
                      {/* Pickup Address */}
                      {ride.pickupAddress && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Icon name="location-dot" size={20} style={{ marginTop: 2 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              {t('rides.dialog.fromLabel')}
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 0.5 }}>
                              {ride.pickupAddress}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      
                      {/* Dropoff Address */}
                      {ride.dropoffAddress && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Icon name="location-dot" size={20} style={{ marginTop: 2 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              {t('rides.dialog.toLabel')}
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 0.5 }}>
                              {ride.dropoffAddress}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      
                      {/* Details Row */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {/* Departure Time */}
                        {ride.departureTime && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '200px' }}>
                            <Icon name="clock" size={20} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                {t('rides.dialog.departureLabel')}
                              </Typography>
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                {ride.departureTime}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Passengers */}
                        {ride.passengers !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '150px' }}>
                            <Icon name="users" size={20} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                {t('rides.dialog.passengersLabel')}
                              </Typography>
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                {ride.passengers}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Status */}
                        {ride.status && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '150px' }}>
                            <Icon name="circle-info" size={20} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                {t('rides.dialog.statusLabel')}
                              </Typography>
                              <Chip
                                label={ride.status}
                                size="small"
                                sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                                color={
                                  ride.status.toLowerCase().includes('cancelled') ||
                                  ride.status.toLowerCase().includes('declined')
                                    ? 'error'
                                    : ride.status.toLowerCase().includes('completed')
                                    ? 'success'
                                    : 'default'
                                }
                              />
                            </Box>
                          </Box>
                        )}
                        
                        {/* Trip Type */}
                        {ride.tripType && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '150px' }}>
                            <Icon name="circle-info" size={20} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                {t('rides.dialog.typeLabel')}
                              </Typography>
                              <Typography variant="body2" sx={{ ml: 0.5, textTransform: 'capitalize' }}>
                                {ride.tripType}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                    
                    {/* Ride ID */}
                    <Box sx={{ pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {t('rides.dialog.idLabel', { value: ride.id.length > 30 ? `${ride.id.substring(0, 30)}...` : ride.id })}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))
            )}
          </Box>
        </Box>

        {/* Progress Indicator */}
        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {currentRide
                ? (operationType === 'upload'
                    ? t('rides.dialog.uploadingCurrent', { value: currentRide })
                    : t('rides.dialog.downloadingCurrent', { value: currentRide }))
                : (operationType === 'upload'
                    ? t('rides.dialog.uploadingDefault')
                    : t('rides.dialog.downloadingDefault'))}
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {/* Acknowledgment Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={isProcessing}
            />
          }
          label={
            <Typography variant="body2">
              {details.acknowledgmentText}
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          {t('rides.dialog.cancel')}
        </Button>
        <Button
          variant="contained"
          color={operationType === 'delete' ? 'error' : 'primary'}
          startIcon={details.actionButtonIcon}
          onClick={handleConfirm}
          disabled={!canProceed}
        >
          {details.actionButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

